import { useI18n } from '@i18n';
import { crrem_version_type_enum } from '@predium/enums';
import { hasValidCrremConfig } from '../sections/Org/Settings/util';
import useOrgPreferences from './useOrgPreferences';

/**
 * Returns version-appropriate translated labels for CRREM stranding/misalignment terminology.
 * CRREM v2.07 renamed "stranding year" to "misalignment year" to better reflect
 * that the concept refers to a building falling out of alignment with the CRREM
 * climate path, rather than a financial stranding event.
 */
export function useCrremTerminology() {
  const { orgPreferences } = useOrgPreferences();
  const { t } = useI18n();
  const isV207 = hasValidCrremConfig(orgPreferences) && orgPreferences.crremVersion === crrem_version_type_enum.V2_07;

  return {
    isV207,
    strandingYear: isV207 ? t('General_MisalignmentYear') : t('General_StrandingYear'),
    strandingDate: isV207 ? t('General_MisalignmentDate') : t('General_StrandingDate'),
    strandingDate15Celsius: isV207 ? t('General_MisalignmentDate15Celsius') : t('General_StrandingDate15Celsius'),
    strandingCRREMPath1_5: isV207 ? t('General_MisalignmentCRREMPath1_5') : t('General_StrandingCRREMPath1_5'),
    strandingBeforeActions: isV207 ? t('General_MisalignmentBeforeActions') : t('General_StrandingBeforeActions'),
    strandingPeriod: isV207 ? t('General_MisalignmentPeriod') : t('General_StrandingPeriod'),
    distributionCsvFilename: isV207
      ? t('MisalignmentYearDistribution-csv-filename')
      : t('StrandingYearDistribution-csv-filename'),
    distributionCsvHeader: isV207
      ? t('MisalignmentYearDistribution-csv-header')
      : t('StrandingYearDistribution-csv-header'),
    distributionTitle: isV207 ? t('MisalignmentYearDistribution-title') : t('StrandingYearDistribution-title'),
    distributionTooltipTitle: isV207
      ? t('MisalignmentYearDistribution-tooltip-title')
      : t('StrandingYearDistribution-tooltip-title'),
    distributionLegendWithActions: isV207
      ? t('MisalignmentYearDistribution_Chart_Legend-with-actions')
      : t('StrandingYearDistribution_Chart_Legend-with-actions'),
    distributionLegendWithoutActions: isV207
      ? t('MisalignmentYearDistribution_Chart_Legend-without-actions')
      : t('StrandingYearDistribution_Chart_Legend-without-actions'),
    distributionTooltipWithActions: isV207
      ? t('MisalignmentYearDistribution_Chart_Tooltip-with-actions')
      : t('StrandingYearDistribution_Chart_Tooltip-with-actions'),
    distributionTooltipWithoutActions: isV207
      ? t('MisalignmentYearDistribution_Chart_Tooltip-without-actions')
      : t('StrandingYearDistribution_Chart_Tooltip-without-actions'),
    portfolioDistributionTitle: isV207
      ? t('StatusQuoAnalysis_PortfolioDistributionByMisalignmentGraph-Title')
      : t('StatusQuoAnalysis_PortfolioDistributionByStrandingGraph-Title'),
    portfolioDistributionSubTitle: isV207
      ? t('StatusQuoAnalysis_PortfolioDistributionByMisalignmentGraph-SubTitle')
      : t('StatusQuoAnalysis_PortfolioDistributionByStrandingGraph-SubTitle'),
    strandingGraphTitle: isV207
      ? t('StatusQuoAnalysis_CRREMAnalysis_MisalignmentGraph-title')
      : t('StatusQuoAnalysis_CRREMAnalysis_StrandingGraph-title'),
    co2IntensityTooltip: isV207
      ? t('StatusQuoAnalysisConsumptionAllocation_CO2IntensityTooltipV207')
      : t('StatusQuoAnalysisConsumptionAllocation_CO2IntensityTooltip'),
  } as const;
}
